import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import styled from 'styled-components'
import { QueryKeys } from 'src/constants/QueryKeys'
import { useAcceptTerms } from 'src/hooks/useAffiliate'
import { AffiliateMe, PublicProgram } from 'src/types/Affiliate'
import { getReason } from 'src/utils/apiReason'
import { formatDay } from 'src/utils/affiliateFormat'
import { errorMessage } from './labels'
import TermsContent from './TermsContent'
import { ActionButton, Card, CardTitle, Checkbox, ErrorText, Muted } from './ui'

const Scroll = styled.div`
    max-height: 420px;
    overflow-y: auto;
    padding: 16px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.15);
`

/** Takes over the Affiliate tab until the current terms version is accepted. */
const TermsGate = ({ me, program }: { me: AffiliateMe; program?: PublicProgram }) => {
    const queryClient = useQueryClient()
    const acceptTerms = useAcceptTerms()
    const [checked, setChecked] = useState(false)
    const terms = me.terms!
    const isUpdate = !!terms.acceptedVersion

    const accept = () =>
        acceptTerms.mutate(terms.currentVersion, {
            onError: (error) => {
                if (getReason(error) === 'terms_outdated') {
                    setChecked(false)
                    queryClient.invalidateQueries({ queryKey: [QueryKeys.affiliateMe] })
                    queryClient.invalidateQueries({ queryKey: [QueryKeys.affiliateProgramPublic] })
                }
            },
        })

    return (
        <Card>
            <CardTitle>{isUpdate ? 'The affiliate terms have been updated' : 'Affiliate terms'}</CardTitle>
            <Muted>
                {isUpdate
                    ? `You accepted version ${terms.acceptedVersion} on ${formatDay(terms.acceptedAt)}. Please review and accept version ${terms.currentVersion} to keep requesting payouts. You keep earning in the meantime.`
                    : `Please review and accept the affiliate terms (version ${terms.currentVersion}) to get started.`}
            </Muted>
            <Scroll>
                <TermsContent program={program} />
            </Scroll>
            <Checkbox>
                <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
                <span>I have read and agree to the affiliate terms (version {terms.currentVersion}).</span>
            </Checkbox>
            {acceptTerms.isError && <ErrorText>{errorMessage(acceptTerms.error)}</ErrorText>}
            <div>
                <ActionButton disabled={!checked || acceptTerms.isPending} onClick={accept}>
                    Accept terms
                </ActionButton>
            </div>
        </Card>
    )
}

export default TermsGate
